import type { Mutation, MutationCreateDatanormRequestMailArgs } from '@ab-core/graphql/dist';
import { useMutation } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { getErrorLoggerFunction } from '../utils';
import { CREATE_DATANORM_REQUEST_MAIL } from './gql/createDatanormRequestMail';

type MutationDataType = Pick<Mutation, 'createDatanormRequestMail'>;
type ResponseDataType = MutationDataType | undefined;

type GeneratedInputType = MutationCreateDatanormRequestMailArgs['datanormRequestMailInput'];

export enum DATANORM_VERSION {
    DATANORM_V40 = 'DATANORM_V40',
    DATANORM_V50 = 'DATANORM_V50',
    ELAB_V2 = 'ELAB_V2',
    EXCEL_LONG = 'EXCEL_LONG',
    EXCEL_SHORT = 'EXCEL_SHORT'
}

export enum DATANORM_PRODUCT_GROUP {
    ALL = 'ALL',
    GROUP_1 = 'GROUP_1',
    GROUP_2 = 'GROUP_2',
    GROUP_3 = 'GROUP_3',
    GROUP_4 = 'GROUP_4',
    GROUP_5 = 'GROUP_5',
    GROUP_6 = 'GROUP_6',
    GROUP_7 = 'GROUP_7',
    GROUP_8 = 'GROUP_8',
    GROUP_9 = 'GROUP_9'
}

export enum DATANORM_INTERVAL {
    MONTHLY = 'MONTHLY',
    QUARTERLY = 'QUARTERLY'
}

export enum DATANORM_DELIVERY_METHOD {
    ONLINE_SHOP = 'ONLINE_SHOP',
    EMAIL = 'EMAIL',
    FTP = 'FTP'
}

export type DatanormRequestMailInputType = Omit<
    GeneratedInputType,
    'productGroups' | 'versions' | 'interval' | 'deliveryMethod' | 'contactPersonPhone'
> & {
    productGroups: DATANORM_PRODUCT_GROUP[];
    versions: DATANORM_VERSION[];
    interval: DATANORM_INTERVAL;
    deliveryMethod: DATANORM_DELIVERY_METHOD;
    contactPersonPhone: string;
};

type MutationVariablesType = { datanormRequestMailInput: DatanormRequestMailInputType };

const onError = getErrorLoggerFunction('createDatanormRequestMail');

export const useCreateDatanormRequestMail = () => {
    const [response, setResponse] = useState<ResponseDataType>(undefined);

    const [createDatanormRequestMail, { loading, error, data }] = useMutation<MutationDataType, MutationVariablesType>(
        CREATE_DATANORM_REQUEST_MAIL,
        {
            onError
        }
    );

    const outgoingCreateDatanormRequestMail = useCallback(
        (datanormRequestMailInput: DatanormRequestMailInputType) =>
            createDatanormRequestMail({ variables: { datanormRequestMailInput } }),
        [createDatanormRequestMail]
    );

    useEffect(() => {
        if (data) {
            setResponse(data);
        }
    }, [data]);

    return {
        success: !!data,
        error: !!error,
        createDatanormRequestMail: outgoingCreateDatanormRequestMail,
        loading,
        response
    };
};
